import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useEmployees } from "../../../api/queries/employeeQueries";
import {
  useCompanies,
  useCompanyHierarchy,
  useBranchHierarchy,
  useBranchesByCompany,
  useAllBranches,
} from "../../../api/queries/organizationQueries";
import { SearchBar } from "../../../components/ui/SearchBar";
import { EMPLOYEE_STATUSES } from "../../../utils/employeeStatus";
import { formatPlainDate } from "../../../utils/dateTime";
import usePermissions from "../../../hooks/usePermissions";

const EmployeeTable = ({ filters, setFilters }) => {
  const navigate = useNavigate();
  // View-only roles don't get Add / Import
  const perms = usePermissions();
  const canModify = perms.can("employees", "modify");
  // A role limited to one assignment type only gets that type: no filter to pick
  const lockedType = perms.employeeTypeOf("employees");
  const storedCompanyId = localStorage.getItem("companyId");
  const storedBranchId = localStorage.getItem("branchId");

  // Company filter: own company tree; with "All companies" (super admin) every company
  const { data: hierarchyOrgs = [] } = useCompanyHierarchy(storedCompanyId);
  const { data: everyCompany = [] } = useCompanies();
  const allOrgs = storedCompanyId ? hierarchyOrgs : everyCompany;
  const { data: allBranches = [] } = useBranchHierarchy(storedBranchId);

  // Company picked in the filter (before Search): its branches fill the Branch dropdown.
  const [pickedCompanyId, setPickedCompanyId] = useState(filters.company || "");
  useEffect(() => {
    setPickedCompanyId(filters.company || "");
  }, [filters.company]);
  // Until a company is picked, a company user's own company (and subsidiaries) fills the list;
  // the super admin on "All companies" has none until one is picked.
  const branchCompanyId = pickedCompanyId || storedCompanyId || "";
  const { data: companyBranches = [] } = useBranchesByCompany(branchCompanyId);
  // "All companies" (no company at all): every branch, labelled with its company
  const { data: everyBranch = [] } = useAllBranches(!branchCompanyId);

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: "firstName",
    direction: "asc",
  });

  const isMultipleBranches = allBranches.length > 1;

  const branchOptions = branchCompanyId
    ? [
        { value: "", label: "All Branches" },
        ...[...companyBranches]
          .sort((a, b) =>
            (a.branchName || "").localeCompare(b.branchName || ""),
          )
          .map((branch) => ({
            value: branch.id,
            label: branch.branchName || branch.name,
          })),
      ]
    : !storedBranchId
      ? [
          { value: "", label: "All Branches" },
          ...[...everyBranch]
            .sort(
              (a, b) =>
                (a.company?.companyName || "").localeCompare(
                  b.company?.companyName || "",
                ) || (a.branchName || "").localeCompare(b.branchName || ""),
            )
            .map((branch) => ({
              value: branch.id,
              label: `${branch.branchName || branch.name}${branch.company?.companyName ? ` — ${branch.company.companyName}` : ""}`,
            })),
        ]
      : isMultipleBranches
        ? [
            { value: storedBranchId, label: "All Branch" },
            ...allBranches
              .filter(
                (branch) =>
                  branch.id !== "All Branch" &&
                  branch.id !== storedBranchId &&
                  !/all/i.test(branch.branchName || branch.name),
              )
              .map((branch) => ({
                value: branch.id,
                label: branch.branchName || branch.name,
              })),
          ]
        : allBranches.map((branch) => ({
            value: branch.id,
            label: branch.branchName || branch.name,
          }));

  const isMultipleCompanies = allOrgs.length > 1;

  const companyOption = isMultipleCompanies
    ? [
        { value: "", label: "All Company" },
        ...allOrgs.map((org) => ({
          value: org.id,
          label: org.companyName || org.name || org,
        })),
      ]
    : allOrgs.map((org) => ({
        value: org.id,
        label: org.companyName || org.name || org,
      }));

  const [selectedCompanyId, setSelectedCompanyId] = useState(
    filters.company || storedCompanyId || "",
  );
  const [selectedBranchId, setSelectedBranchId] = useState(
    filters.branch || storedBranchId || "",
  );

  // Query params: URL filters mapped to API names, plus company and paging.
  const employeeQuery = useMemo(() => {
    const cleanFilters = {};
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (
        value !== undefined &&
        value !== null &&
        value !== "All" &&
        value !== "both" &&
        String(value).trim() !== ""
      ) {
        const trimmedValue = String(value).trim();
        if (key === "branch") cleanFilters.branchId = trimmedValue;
        else if (key === "company") cleanFilters.companyId = trimmedValue;
        else if (key === "client") cleanFilters.clientId = trimmedValue;
        else if (key === "role") cleanFilters.roleId = trimmedValue;
        else cleanFilters[key] = trimmedValue;
      }
    });
    return {
      ...cleanFilters,
      companyId: selectedCompanyId || storedCompanyId,
      page: currentPage,
      limit: itemsPerPage,
    };
  }, [filters, currentPage, itemsPerPage, selectedCompanyId, storedCompanyId]);

  const employeesQuery = useEmployees(employeeQuery);
  const currentData = employeesQuery.data?.data || [];
  const meta = employeesQuery.data?.meta || {};
  const totalItems = Number(meta.total ?? currentData.length);
  const totalPages = Number(
    meta.totalPages ?? (Math.ceil(totalItems / itemsPerPage) || 1),
  );
  const isLoading = employeesQuery.isLoading;

  const toCamelCase = (str) => {
    if (!str) return "Active";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const getStatusStyle = (empStatus) => {
    const s = empStatus?.toLowerCase();
    if (s === "active" || s === "complete")
      return {
        bg: "bg-success",
        text: "text-success",
        border: "border-success",
      };
    if (["inactive", "resigned", "terminated", "awol"].includes(s))
      return { bg: "bg-danger", text: "text-danger", border: "border-danger" };
    if (["incomplete", "separated"].includes(s))
      return {
        bg: "bg-warning",
        text: "text-warning",
        border: "border-warning",
      };
    return {
      bg: "bg-secondary",
      text: "text-secondary",
      border: "border-secondary",
    };
  };

  // Sync company/branch state if external filters change (e.g. Back button)
  useEffect(() => {
    if (
      filters.company !== undefined &&
      filters.company !== selectedCompanyId
    ) {
      setSelectedCompanyId(filters.company);
    }
    if (filters.branch !== undefined && filters.branch !== selectedBranchId) {
      setSelectedBranchId(filters.branch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleSearch = (searchValues) => {
    setCurrentPage(1);

    const activeFilters = {
      ...filters,
      ...searchValues,
      company:
        searchValues.company !== undefined
          ? searchValues.company
          : selectedCompanyId || filters.company,
      branch:
        searchValues.branch !== undefined
          ? searchValues.branch
          : selectedBranchId || filters.branch,
      assignmentType:
        searchValues.assignmentType !== undefined
          ? searchValues.assignmentType
          : searchValues.assignmentType || filters.assignmentType,
    };

    // Update internal structure identifiers if they changed
    if (activeFilters.company !== undefined)
      setSelectedCompanyId(activeFilters.company);
    if (activeFilters.branch !== undefined)
      setSelectedBranchId(activeFilters.branch);

    // This updates the parent state and triggers URL params updates
    if (setFilters) {
      setFilters(activeFilters);
    }
  };

  const handlePageChange = useCallback((targetPage, overrideLimit = null) => {
    setCurrentPage(targetPage);
    if (overrideLimit !== null) {
      setItemsPerPage(overrideLimit);
    }
  }, []);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc")
      direction = "desc";
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key)
      return <i className="bi bi-arrow-down-up ms-2 opacity-25 fs-060"></i>;
    return sortConfig.direction === "asc" ? (
      <i className="bi bi-sort-down ms-2 text-primary fs-070"></i>
    ) : (
      <i className="bi bi-sort-up ms-2 text-primary fs-070"></i>
    );
  };

  return (
    <div className="w-100 position-relative">
      {isLoading && (
        <div className="page-loading-overlay">
          <div className="spinner-border text-primary"></div>
        </div>
      )}

      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="fw-bold text-dark m-0 page-title">Employees</h1>
            <p className="text-muted small mb-0">Manage employee records</p>
          </div>
          {canModify && (
            <div className="d-flex gap-2">
              <button
                className="btn btn-primary px-4 border-0 btn-navy"
                onClick={() => navigate("/employee/new")}
              >
                <i className="bi bi-plus-lg me-2"></i>Add New
              </button>
              <button
                className="btn btn-outline-secondary px-4 bg-white shadow-sm rounded-8 fs-085 fw-semibold border-soft"
                onClick={() => navigate("/employee/import")}
              >
                <i className="bi bi-file-earmark-arrow-up me-2"></i>Import
              </button>
            </div>
          )}
        </div>

        {filters.hiredFrom && (
          <div className="d-flex flex-wrap gap-2 mb-2">
            <span className="chip">
              Hired since {formatPlainDate(filters.hiredFrom)}
              <button
                type="button"
                className="chip-remove"
                onClick={() => {
                  setCurrentPage(1);
                  setFilters({ ...filters, hiredFrom: "" });
                }}
                aria-label="Remove the hired-since filter"
              >
                <i className="bi bi-x"></i>
              </button>
            </span>
          </div>
        )}
        {lockedType && (
          <p className="fs-080 text-slate-600 mb-2">
            <i className="bi bi-funnel me-1"></i>
            Your role shows {lockedType.toLowerCase()} employees only.
          </p>
        )}
        {/* SearchBar lets user type freely without triggering fetching until onSearch is clicked */}
        <SearchBar
          filters={filters}
          setFilters={setFilters}
          onFieldChange={(key, value) => {
            if (key !== "company") return null;
            setPickedCompanyId(value && value !== "All" ? value : "");
            return { branch: "" }; // the old branch may not belong to the new company
          }}
          fields={[
            {
              key: "search",
              label: "Name",
              type: "text",
              placeholder: "Search Name...",
            },
            {
              key: "department",
              label: "Department",
              type: "text",
              placeholder: "Search Dept...",
            },
            {
              key: "branch",
              label: "Branch",
              defaultValue: storedBranchId || "",
              type: "select",
              options: branchOptions,
            },
            {
              key: "company",
              label: "Company",
              defaultValue: "All",
              type: "select",
              options: companyOption,
            },
            {
              key: "status",
              label: "Status",
              type: "select",
              defaultValue: "All",
              options: ["All", ...EMPLOYEE_STATUSES],
            },
            ...(lockedType
              ? []
              : [
                  {
                    key: "assignmentType",
                    label: "Assignment Type",
                    type: "select",
                    defaultValue: "All",
                    options: ["All", "Internal", "Deployed"],
                  },
                ]),
          ]}
          onSearch={handleSearch}
        ></SearchBar>

        <div className="glass-table-card">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="custom-table-header sortable">
                <tr>
                  <th
                    className="py-3 ps-4 border-0"
                    onClick={() => requestSort("firstName")}
                  >
                    EMPLOYEE NAME {getSortIcon("firstName")}
                  </th>
                  <th
                    className="py-3 border-0"
                    onClick={() => requestSort("department")}
                  >
                    DEPARTMENT {getSortIcon("department")}
                  </th>
                  <th
                    className="py-3 border-0"
                    onClick={() => requestSort("branch")}
                  >
                    BRANCH {getSortIcon("branch")}
                  </th>
                  <th
                    className="py-3 border-0"
                    onClick={() => requestSort("company")}
                  >
                    COMPANY {getSortIcon("company")}
                  </th>
                  <th className="py-3 border-0 text-center">STATUS</th>
                  <th className="py-3 border-0 text-center">LEAVE STATUS</th>
                  <th
                    className="py-3 border-0"
                    onClick={() => requestSort("createdAt")}
                  >
                    CREATED AT {getSortIcon("createdAt")}
                  </th>
                  <th className="py-3 pe-4 border-0 text-end">ACTIONS</th>
                </tr>
              </thead>
              <tbody className="border-top-0">
                {currentData.map((emp) => {
                  const sStyle = getStatusStyle(emp.employeeStatus);
                  return (
                    <tr key={emp.id} className="align-middle">
                      <td className="ps-4 py-3">
                        <div className="emp-name-text">
                          {emp.firstName} {emp.lastName}
                        </div>
                        <div className="emp-sub-text">
                          ID: {emp.employeeId}-{emp.branch?.BranchCode || "HQ"}
                          <br />
                          {emp.companyEmail || "-"}
                        </div>
                      </td>
                      <td className="table-cell-text">
                        {emp.department?.departmentName || "-"}
                      </td>
                      <td className="table-cell-text">
                        {emp.branch?.branchName || "-"}
                      </td>
                      <td className="table-cell-text">
                        {emp.company?.companyName || "-"}
                      </td>
                      <td className="text-center">
                        <span
                          className={`badge rounded-pill ${sStyle.bg} bg-opacity-10 ${sStyle.text} border ${sStyle.border} border-opacity-25 badge-custom`}
                        >
                          {toCamelCase(emp.employeeStatus)}
                        </span>
                      </td>
                      <td className="text-center">
                        <span
                          className={`badge rounded-pill ${sStyle.bg} bg-opacity-10 ${sStyle.text} border ${sStyle.border} border-opacity-25 badge-custom`}
                        >
                          {toCamelCase(emp.leaveStatus)}
                        </span>
                      </td>
                      <td className="table-cell-text">
                        {emp.createdAt
                          ? new Date(emp.createdAt).toLocaleDateString()
                          : "-"}
                      </td>
                      <td className="text-end pe-4">
                        <button
                          className="btn btn-sm btn-outline-primary fw-bold fs-075 rounded-6"
                          onClick={() => navigate(`/employee/${emp.id}`)}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {currentData.length === 0 && (
                  <tr>
                    <td
                      colSpan="8"
                      className="text-center py-5 text-muted small"
                    >
                      No employees found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination-container d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 p-3">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted fs-075">Show</span>
              <select
                className="items-select"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {[10, 25, 50].map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
              <span className="text-muted fs-075">
                of <strong>{(totalItems || 0).toLocaleString()}</strong> entries
              </span>
            </div>

            <nav className="d-flex align-items-center">
              {/* Previous Button */}
              <button
                type="button"
                className={`page-link-custom ${Number(currentPage) <= 1 ? "disabled" : ""}`}
                onClick={() => {
                  const prevNum = Number(currentPage) - 1;
                  if (prevNum >= 1) handlePageChange(prevNum);
                }}
                disabled={Number(currentPage) <= 1}
              >
                <i className="bi bi-chevron-left fs-080"></i>
              </button>

              {/* Dynamic Page Numbers Generation */}
              {(() => {
                const current = Number(currentPage);
                const pages = [];
                const maxVisiblePages = 5;

                let startPage = Math.max(
                  1,
                  current - Math.floor(maxVisiblePages / 2),
                );
                let endPage = Math.min(
                  totalPages,
                  startPage + maxVisiblePages - 1,
                );

                if (endPage - startPage + 1 < maxVisiblePages) {
                  startPage = Math.max(1, endPage - maxVisiblePages + 1);
                }

                // Always show page 1
                if (startPage > 1) {
                  pages.push(
                    <button
                      key={1}
                      type="button"
                      className={`page-link-custom ${current === 1 ? "active" : ""}`}
                      onClick={() => handlePageChange(1)}
                    >
                      1
                    </button>,
                  );
                  if (startPage > 2) {
                    pages.push(
                      <span key="dots-start" className="px-2 text-muted fs-075">
                        ...
                      </span>,
                    );
                  }
                }

                // Render middle pages
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <button
                      key={i}
                      type="button"
                      className={`page-link-custom ${current === i ? "active" : ""}`}
                      onClick={() => handlePageChange(i)}
                    >
                      {i}
                    </button>,
                  );
                }

                // Always show last page
                if (endPage < totalPages) {
                  if (endPage < totalPages - 1) {
                    pages.push(
                      <span key="dots-end" className="px-2 text-muted fs-075">
                        ...
                      </span>,
                    );
                  }
                  pages.push(
                    <button
                      key={totalPages}
                      type="button"
                      className={`page-link-custom ${current === totalPages ? "active" : ""}`}
                      onClick={() => handlePageChange(totalPages)}
                    >
                      {totalPages}
                    </button>,
                  );
                }

                return pages;
              })()}

              {/* Next Button */}
              <button
                type="button"
                className={`page-link-custom ${Number(currentPage) >= totalPages ? "disabled" : ""}`}
                onClick={() => {
                  const nextNum = Number(currentPage) + 1;
                  if (nextNum <= totalPages) handlePageChange(nextNum);
                }}
                disabled={Number(currentPage) >= totalPages}
              >
                <i className="bi bi-chevron-right fs-080"></i>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeTable;
